import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { FormDialog } from '@/components/shared/form-dialog';
import { Pagination } from '@/components/shared/pagination';
import { ensureProgramOccurrenceId } from '@/components/programs/ensure-occurrence';
import {
  type Gathering,
  upcomingGatheringDates,
} from '@/components/programs/gathering-schedule';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/shared/form-field';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import { buildProgramRegistrationUrl } from '@/lib/public-origins';
import { useChurchStore } from '@/stores/church-store';
import { formatDate, churchToday, formatMoney } from '@/utils/format';

interface RegistrationRow {
  id: string;
  occurrenceId: string;
  occurrenceDate?: string;
  memberId: string;
  firstName: string;
  lastName: string;
  status: string;
  guestCount: number;
  paymentIntentId: string | null;
  selectedPaymentMethod: string | null;
  contributionId: string | null;
  paymentStatus: 'awaiting_cash' | 'awaiting_online' | 'paid' | 'not_required';
  registeredAt: string;
  source: string;
}

interface InviteRow {
  id: string;
  memberId: string;
  firstName: string;
  lastName: string;
}

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

export interface ProgramRegistrationSettings {
  registrationAccess?: 'open' | 'invite_only';
  registrationFeeType?: 'free' | 'paid';
  registrationFeeAmountMinor?: number | null;
  registrationFeeCurrency?: string | null;
  registrationAllowedPaymentMethods?: Array<'mobile_money' | 'card' | 'cash'>;
  capacity?: number | null;
}

export interface ProgramRegistrationsSectionProps {
  programId: string;
  registrationMode?: 'none' | 'rsvp' | 'registration';
  registrationSettings?: ProgramRegistrationSettings;
}

export const ProgramRegistrationsSection = ({
  programId,
  registrationMode = 'none',
  registrationSettings,
}: ProgramRegistrationsSectionProps) => {
  const registrationAccess = registrationSettings?.registrationAccess ?? 'open';
  const { t } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const church = useChurchStore((state) => state.church);
  const canManage = hasPermission(membership, PERMISSIONS.PROGRAMS_MANAGE_REGISTRATIONS);
  const canRecordGiving = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const canReconcileCash = canManage && canRecordGiving;
  const canEditInvites = hasPermission(membership, PERMISSIONS.PROGRAMS_EDIT);

  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberSelected, setMemberSelected] = useState<{ id: string; label: string } | null>(null);
  const [guestCount, setGuestCount] = useState('0');
  const [cancelTarget, setCancelTarget] = useState<RegistrationRow | null>(null);
  const [inviteEditOpen, setInviteEditOpen] = useState(false);
  const [inviteMemberIds, setInviteMemberIds] = useState<string[]>([]);

  const registrations = useQuery({
    queryKey: ['programs', programId, 'registrations', page],
    queryFn: () =>
      api.get<{
        data: RegistrationRow[];
        page: { totalItems: number; pageSize: number };
        totals?: {
          collectedAmountMinor: number;
          currency: string | null;
          paidCount: number;
        };
      }>(`/programs/${programId}/registrations?page=${page}&pageSize=25`),
    enabled: registrationMode !== 'none' && Boolean(programId),
  });

  useEffect(() => {
    if (registrationMode === 'none' || !programId) return;
    void api
      .post<{ synced: number }>(`/programs/${programId}/registrations/sync-payments`)
      .then((result) => {
        if (result.synced > 0) {
          void queryClient.invalidateQueries({
            queryKey: ['programs', programId, 'registrations'],
          });
        }
      })
      .catch(() => undefined);
  }, [programId, registrationMode, queryClient]);

  const occurrences = useQuery({
    queryKey: ['occurrences', 'program', programId, 'upcoming'],
    queryFn: () => {
      const params = new URLSearchParams({
        programId,
        status: 'scheduled',
        page: '1',
        pageSize: '1',
        sort: 'occurrenceDate',
        direction: 'asc',
        fromDate: churchToday(),
      });
      return api.get<{
        data: Array<{ id: string; occurrenceDate: string; status: string }>;
      }>(`/occurrences?${params}`);
    },
    enabled: addOpen,
  });

  const program = useQuery({
    queryKey: ['programs', programId],
    queryFn: () => api.get<Gathering>(`/programs/${programId}`),
    enabled: registrationMode !== 'none' && Boolean(programId),
  });

  useEffect(() => {
    if (!program.data || registrationMode === 'none') return;
    const nextDate = upcomingGatheringDates(program.data, churchToday(), 1)[0]?.date;
    if (!nextDate) return;
    void ensureProgramOccurrenceId({ programId, occurrenceDate: nextDate });
  }, [program.data, programId, registrationMode]);

  const nextRegistrationDate = useMemo(() => {
    const fromOccurrence = occurrences.data?.data[0]?.occurrenceDate;
    if (fromOccurrence) return fromOccurrence;
    if (!program.data) return null;
    return upcomingGatheringDates(program.data, churchToday(), 1)[0]?.date ?? null;
  }, [occurrences.data?.data, program.data]);

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'registration', memberQuery],
    queryFn: () => {
      const params = new URLSearchParams({ page: '1', pageSize: '20' });
      if (memberQuery.trim()) params.set('q', memberQuery.trim());
      return api.get<{ data: MemberOption[] }>(`/people/members?${params.toString()}`);
    },
    enabled: addOpen || inviteEditOpen,
  });

  const invites = useQuery({
    queryKey: ['programs', programId, 'registration-invites'],
    queryFn: () =>
      api.get<{ data: InviteRow[] }>(`/programs/${programId}/registration-invites`),
    enabled: registrationAccess === 'invite_only' && Boolean(programId),
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );

  const addRegistration = useMutation({
    mutationFn: async () => {
      if (!memberId) {
        throw new Error('missing');
      }

      let occurrenceId = occurrences.data?.data[0]?.id;
      if (!occurrenceId) {
        const date =
          nextRegistrationDate ??
          (program.data
            ? upcomingGatheringDates(program.data, churchToday(), 1)[0]?.date
            : null);
        if (!date) {
          throw new Error('REGISTRATION_CLOSED');
        }
        occurrenceId = await ensureProgramOccurrenceId({ programId, occurrenceDate: date });
      }

      return api.post(`/occurrences/${occurrenceId}/registrations`, {
        memberId,
        guestCount: Number(guestCount) || 0,
      });
    },
    onSuccess: async () => {
      toast.success(t('registration.added'));
      setAddOpen(false);
      setMemberId(null);
      setMemberSelected(null);
      setGuestCount('0');
      await queryClient.invalidateQueries({ queryKey: ['programs', programId, 'registrations'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const reconcileCash = useMutation({
    mutationFn: (registrationId: string) =>
      api.post(`/registrations/${registrationId}/reconcile-cash`),
    onSuccess: async () => {
      toast.success(t('registration.cashReconciled'));
      await queryClient.invalidateQueries({ queryKey: ['programs', programId, 'registrations'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const cancelRegistration = useMutation({
    mutationFn: (registrationId: string) =>
      api.post(`/registrations/${registrationId}/cancel`),
    onSuccess: async () => {
      toast.success(t('registration.cancelled'));
      setCancelTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['programs', programId, 'registrations'] });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  const saveInvites = useMutation({
    mutationFn: () =>
      api.patch(`/programs/${programId}/registration-invites`, { memberIds: inviteMemberIds }),
    onSuccess: async () => {
      toast.success(t('registration.invitesSaved'));
      setInviteEditOpen(false);
      await queryClient.invalidateQueries({
        queryKey: ['programs', programId, 'registration-invites'],
      });
    },
    onError: (error) => toast.error(resolveApiError(error, tErrors)),
  });

  if (registrationMode === 'none') return null;

  const statusLabel = (status: string) => t(`registration.status.${status}`, { defaultValue: status });

  const paymentLabel = (row: RegistrationRow) => {
    switch (row.paymentStatus) {
      case 'awaiting_cash':
        return t('registration.payment.awaitingCash');
      case 'awaiting_online':
        return t('registration.payment.awaitingOnline');
      case 'paid':
        return t('registration.payment.paid');
      default:
        return '—';
    }
  };

  const sourceLabel = (source: string) =>
    t(`registration.source.${source}`, { defaultValue: source });

  const columns: DataTableColumn<RegistrationRow>[] = [
    {
      id: 'occurrenceDate',
      header: t('registration.date'),
      cell: (row) => (row.occurrenceDate ? formatDate(row.occurrenceDate) : '—'),
      hideBelowMd: !registrations.data?.data.some((row) => row.occurrenceDate),
    },
    {
      id: 'name',
      header: t('registration.name'),
      cell: (row) => (
        <MemberNameLink
          memberId={row.memberId}
          name={`${row.firstName} ${row.lastName}`.trim()}
        />
      ),
    },
    {
      id: 'source',
      header: t('registration.sourceLabel'),
      cell: (row) => sourceLabel(row.source),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('registration.statusLabel'),
      cell: (row) => <Badge variant="neutral">{statusLabel(row.status)}</Badge>,
    },
    {
      id: 'payment',
      header: t('registration.paymentLabel'),
      cell: (row) => paymentLabel(row),
      hideBelowMd: true,
    },
    {
      id: 'guestCount',
      header: t('registration.guests'),
      cell: (row) => String(row.guestCount),
      hideBelowMd: true,
    },
  ];

  const totalItems = registrations.data?.page.totalItems ?? 0;
  const pageSize = registrations.data?.page.pageSize ?? 25;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const paymentMethodLabels = useMemo(() => {
    const methods = registrationSettings?.registrationAllowedPaymentMethods ?? [];
    return methods.map((method) => {
      if (method === 'mobile_money') return t('registration.mobileMoney');
      if (method === 'card') return t('registration.card');
      return t('registration.cash');
    });
  }, [registrationSettings?.registrationAllowedPaymentMethods, t]);

  const feeLabel = useMemo(() => {
    if (registrationSettings?.registrationFeeType !== 'paid') {
      return t('registration.feeFree');
    }
    const amount = registrationSettings.registrationFeeAmountMinor;
    const currency = registrationSettings.registrationFeeCurrency;
    if (amount == null || !currency) return t('registration.feePaid');
    return formatMoney(amount, currency);
  }, [registrationSettings, t]);

  const collectedTotals = registrations.data?.totals;
  const showCollected =
    collectedTotals != null &&
    collectedTotals.collectedAmountMinor > 0 &&
    collectedTotals.currency != null;

  const registrationUrl = church?.slug
    ? buildProgramRegistrationUrl(church.slug, programId)
    : null;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{t('registration.title')}</CardTitle>
            <CardDescription>{t('registration.description')}</CardDescription>
          </div>
          {canManage ? (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              {t('registration.add')}
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-4">
          {registrationSettings ? (
            <div className="rounded-lg border border-border p-4">
              <p className="text-body-sm font-medium text-fg">{t('registration.settingsTitle')}</p>
              <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                <div>
                  <dt className="text-caption text-fg-secondary">{t('registration.settingsAccess')}</dt>
                  <dd className="text-body-sm text-fg">
                    {registrationAccess === 'invite_only'
                      ? t('registration.settingsAccessInviteOnly')
                      : t('registration.settingsAccessOpen')}
                  </dd>
                </div>
                <div>
                  <dt className="text-caption text-fg-secondary">{t('registration.feeType')}</dt>
                  <dd className="text-body-sm text-fg">{feeLabel}</dd>
                </div>
                <div>
                  <dt className="text-caption text-fg-secondary">{t('registration.capacity')}</dt>
                  <dd className="text-body-sm text-fg">
                    {registrationSettings.capacity
                      ? registrationSettings.capacity
                      : t('registration.settingsCapacityUnlimited')}
                  </dd>
                </div>
                {registrationSettings.registrationFeeType === 'paid' &&
                paymentMethodLabels.length > 0 ? (
                  <div>
                    <dt className="text-caption text-fg-secondary">
                      {t('registration.paymentMethods')}
                    </dt>
                    <dd className="text-body-sm text-fg">{paymentMethodLabels.join(' · ')}</dd>
                  </div>
                ) : null}
                {showCollected ? (
                  <div className="sm:col-span-2">
                    <dt className="text-caption text-fg-secondary">
                      {t('registration.collectedSoFar')}
                    </dt>
                    <dd className="text-body-sm font-medium text-fg">
                      {formatMoney(
                        collectedTotals.collectedAmountMinor,
                        collectedTotals.currency!,
                      )}
                      <span className="ml-2 font-normal text-fg-secondary">
                        {t('registration.collectedCount', { count: collectedTotals.paidCount })}
                      </span>
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          ) : null}
          {registrationUrl ? (
            <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-sunken p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-body-sm font-medium text-fg">{t('registration.shareLink')}</p>
                <p className="text-caption text-fg-secondary">{t('registration.shareLinkHint')}</p>
                <p className="mt-1 truncate font-mono text-caption text-fg-secondary">
                  {registrationUrl}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="shrink-0"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(registrationUrl);
                    toast.success(t('registration.shareLinkCopied'));
                  } catch {
                    toast.error(t('registration.shareLinkCopyFailed'));
                  }
                }}
              >
                {t('registration.copyShareLink')}
              </Button>
            </div>
          ) : null}
          <DataTable
            columns={columns}
            rows={registrations.data?.data ?? []}
            rowKey={(row) => row.id}
            loading={registrations.isLoading}
            empty={<p className="text-body-sm text-fg-secondary">{t('registration.empty')}</p>}
            rowActions={
              canManage
                ? (row) => (
                    <div className="flex justify-end gap-1">
                      {canReconcileCash &&
                      row.status === 'pending_payment' &&
                      !row.contributionId &&
                      !row.paymentIntentId ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={reconcileCash.isPending}
                          onClick={() => reconcileCash.mutate(row.id)}
                        >
                          {t('registration.recordCash')}
                        </Button>
                      ) : null}
                      {row.status !== 'cancelled' && row.status !== 'attended' ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setCancelTarget(row)}
                        >
                          {t('registration.cancel')}
                        </Button>
                      ) : null}
                    </div>
                  )
                : undefined
            }
          />
          {totalItems > pageSize ? (
            <Pagination
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              totalPages={totalPages}
              onPageChange={setPage}
              labels={{
                showing: ({ from, to, total }) => `${from}–${to} / ${total}`,
                perPage: tCommon('pagination.perPage', { defaultValue: 'Per page' }),
                first: tCommon('pagination.first', { defaultValue: 'First' }),
                previous: tCommon('pagination.previous', { defaultValue: 'Previous' }),
                next: tCommon('pagination.next', { defaultValue: 'Next' }),
                last: tCommon('pagination.last', { defaultValue: 'Last' }),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      {registrationAccess === 'invite_only' ? (
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>{t('registration.invitesTitle')}</CardTitle>
              <CardDescription>{t('registration.invitesDescription')}</CardDescription>
            </div>
            {canEditInvites ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setInviteMemberIds((invites.data?.data ?? []).map((row) => row.memberId));
                  setInviteEditOpen(true);
                }}
              >
                {t('registration.editInvites')}
              </Button>
            ) : null}
          </CardHeader>
          <CardContent>
            {(invites.data?.data ?? []).length === 0 ? (
              <p className="text-body-sm text-fg-secondary">{t('registration.invitesEmpty')}</p>
            ) : (
              <ul className="space-y-1 text-body-sm">
                {(invites.data?.data ?? []).map((invite) => (
                  <li key={invite.id}>
                    {invite.firstName} {invite.lastName}
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ) : null}

      <FormDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        title={t('registration.add')}
        closeLabel={tCommon('action.close')}
        cancelLabel={tCommon('action.cancel')}
        submitLabel={t('registration.addConfirm')}
        submitting={addRegistration.isPending}
        submitDisabled={!memberId || !nextRegistrationDate}
        onSubmit={() => addRegistration.mutate()}
      >
        <div className="space-y-4">
          {nextRegistrationDate ? (
            <p className="text-body-sm text-fg-secondary">
              {t('registration.addForDate', { date: formatDate(nextRegistrationDate) })}
            </p>
          ) : null}
          <FormField label={t('registration.member')}>
            {(field) => (
              <EntityCombobox
                id={field.id}
                value={memberId}
                onChange={(id) => {
                  setMemberId(id);
                  setMemberSelected(memberOptions.find((row) => row.id === id) ?? null);
                }}
                options={memberOptions}
                selected={memberSelected}
                onQueryChange={setMemberQuery}
                loading={members.isFetching}
                placeholder={t('registration.memberSearch')}
                popoverPortal="dialog"
              />
            )}
          </FormField>
          <FormField label={t('registration.guests')} hint={t('registration.guestsHint')}>
            {(field) => (
              <Input
                id={field.id}
                inputMode="numeric"
                value={guestCount}
                onChange={(event) => setGuestCount(event.target.value.replace(/\D/g, ''))}
              />
            )}
          </FormField>
        </div>
      </FormDialog>

      <FormDialog
        open={inviteEditOpen}
        onOpenChange={setInviteEditOpen}
        title={t('registration.editInvites')}
        closeLabel={tCommon('action.close')}
        cancelLabel={tCommon('action.cancel')}
        submitLabel={tCommon('action.save')}
        submitting={saveInvites.isPending}
        onSubmit={() => saveInvites.mutate()}
      >
        <FormField label={t('registration.inviteMembers')}>
          {(field) => (
            <EntityCombobox
              id={field.id}
              value={null}
              onChange={(id) => {
                if (!id) return;
                setInviteMemberIds((current) =>
                  current.includes(id) ? current : [...current, id],
                );
              }}
              options={memberOptions.filter((option) => !inviteMemberIds.includes(option.id))}
              onQueryChange={setMemberQuery}
              loading={members.isFetching}
              placeholder={t('registration.memberSearch')}
              popoverPortal="dialog"
            />
          )}
        </FormField>
        {inviteMemberIds.length > 0 ? (
          <ul className="space-y-2">
            {inviteMemberIds.map((id) => {
              const invite = invites.data?.data.find((row) => row.memberId === id);
              const label =
                memberOptions.find((option) => option.id === id)?.label ??
                (invite ? `${invite.firstName} ${invite.lastName}`.trim() : id);
              return (
                <li key={id} className="flex items-center justify-between gap-2 text-body-sm">
                  <span>{label}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setInviteMemberIds((current) => current.filter((memberId) => memberId !== id))
                    }
                  >
                    {t('registration.removeInvite')}
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-body-sm text-fg-secondary">{t('registration.invitesEmpty')}</p>
        )}
      </FormDialog>

      <ConfirmDialog
        open={Boolean(cancelTarget)}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null);
        }}
        title={t('registration.cancelConfirmTitle', {
          name: cancelTarget ? `${cancelTarget.firstName} ${cancelTarget.lastName}`.trim() : '',
        })}
        description={t('registration.cancelConfirmDescription')}
        confirmLabel={t('registration.cancel')}
        cancelLabel={tCommon('action.cancel')}
        loading={cancelRegistration.isPending}
        onConfirm={() => {
          if (cancelTarget) cancelRegistration.mutate(cancelTarget.id);
        }}
      />
    </>
  );
};
